import { AlertCircle } from "lucide-react";

// BootsmanResult type definition (duplicated to avoid import issues)
export type BootsmanResult<T = unknown> =
  | {
      success: true;
      data: T;
      message?: string;
    }
  | {
      success: false;
      error: {
        code: string;
        message: string;
        details?: unknown;
      };
    };

interface ToolErrorProps {
  result: unknown;
}

// Type guard for BootsmanResult
export function isBootsmanResult(value: unknown): value is BootsmanResult {
  if (!value || typeof value !== "object") {
    return false;
  }

  const obj = value as Record<string, unknown>;

  if ("success" in obj && typeof obj.success === "boolean") {
    if (obj.success) {
      return "data" in obj;
    }
    return (
      "error" in obj &&
      typeof obj.error === "object" &&
      obj.error !== null &&
      "code" in obj.error &&
      "message" in obj.error
    );
  }

  return false;
}

export function ToolError({ result }: ToolErrorProps) {
  const errorMessage = (() => {
    if (isBootsmanResult(result) && !result.success) {
      return result.error.message;
    }
    // Fallback for non-BootsmanResult errors
    if (result && typeof result === "object" && "error" in result) {
      const error = (result as Record<string, unknown>).error;
      if (typeof error === "string") {
        return error;
      }
      if (typeof error === "object" && error && "message" in error) {
        return String(error.message);
      }
    }
    return "Tool execution failed";
  })();

  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <AlertCircle className="w-3 h-3 flex-shrink-0 text-warning" />
      <span className="text-xs">{errorMessage}</span>
    </div>
  );
}
